import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import type SensitiveFieldsVisibilityService from 'garaje/services/sensitive-fields-visibility';

interface SensitiveRevealToggleSignature {
  Args: {
    // The sensitive field key to reveal/hide, e.g. 'Host'. Reveal state is shared
    // across every toggle bound to the same key (one boolean in the service), so
    // multiple eyes for the same field — the column header and the Name-cell eye —
    // always render and flip together.
    name: string;
  };
  Element: HTMLButtonElement;
}

export default class SensitiveRevealToggle extends Component<SensitiveRevealToggleSignature> {
  @service declare sensitiveFieldsVisibility: SensitiveFieldsVisibilityService;

  get isRevealed(): boolean {
    return this.sensitiveFieldsVisibility.isRevealed(this.args.name);
  }

  // Describes the action the click will take, so it reads correctly for the current
  // state; used for both the accessible label and the hover tooltip.
  get label(): string {
    return this.isRevealed ? 'Hide sensitive field' : 'Show sensitive field';
  }

  toggleRevealed = (): void => {
    this.sensitiveFieldsVisibility.toggle(this.args.name);
  };

  <template>
    <button
      type="button"
      class="inline-flex items-center ml-4 text-carbon-50"
      aria-label={{this.label}}
      aria-pressed={{if this.isRevealed "true" "false"}}
      data-test-sensitive-column-toggle
      {{on "click" this.toggleRevealed}}
      ...attributes
    >
      {{svgJar
        (if this.isRevealed "sensitive-eye-slashed" "open-eye-grayLight")
        width="16"
        height="16"
        class="[&_path]:fill-current [&_circle]:fill-current"
      }}
      <EmberTooltip @side="top" @delay={{200}} @popperContainer="body">
        {{this.label}}
      </EmberTooltip>
    </button>
  </template>
}
